/*
 * /auth/account (page)
 */
import { supabase } from "@psychology/auth/client"
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
	Divider,
	Input,
	Label,
	Page,
	toast,
} from "@psychology/react-ui"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { trpc } from "../../trpc"
import { authPageLoaderAuthenticated } from "../../utils/auth.utils"
import { appConfig } from "../../config/app-config"

export const Route = createFileRoute("/auth/account")({
	component: AccountPage,
	loader: authPageLoaderAuthenticated(),
})

function AccountPage() {
	const {
		user: { id, email },
	} = Route.useLoaderData()
	const { t } = useTranslation()
	const navigate = useNavigate()

	const { data: user, isLoading } = trpc.user.getByFilter.useQuery({
		where: {},
	})

	const utils = trpc.useUtils()
	const updateUser = trpc.user.update.useMutation({
		onSuccess: () => {
			utils.user.getByFilter.invalidate()
			toast.success(t("auth.updateSuccess"))
		},
		onError: (err) => {
			toast.error(err.message)
		},
	})

	const [preferredName, setPreferredName] = useState("")
	const [avatarUrl, setAvatarUrl] = useState("")

	useEffect(() => {
		if (user) {
			setPreferredName(user.preferredName ?? "")
			setAvatarUrl(user.avatarUrl ?? "")
		}
	}, [user])

	function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault()
		updateUser.mutate({ id, preferredName, avatarUrl: avatarUrl || null })
	}

	async function handleSignOut() {
		await supabase.auth.signOut()
		navigate({ to: appConfig.redirects.afterSignOutUrl })
	}

	const saving = updateUser.isPending

	return (
		<Page className={"bg-primary/30"}>
			<Card className={"w-full max-w-md p-8 pb-12"}>
				<CardHeader className={"items-center gap-2 pb-6"}>
					<CardTitle>{t("auth.account")}</CardTitle>
					<CardDescription>{email}</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit} className={"flex flex-col gap-6"}>
						<div className={"grid gap-2"}>
							<Label htmlFor={"preferred-name"}>{t("auth.name")}</Label>
							<Input
								id={"preferred-name"}
								type={"text"}
								placeholder={"John Doe"}
								value={preferredName}
								onChange={(e) => setPreferredName(e.target.value)}
								disabled={isLoading}
								required
							/>
						</div>
						<div className={"grid gap-2"}>
							<Label htmlFor={"avatar-url"}>{t("auth.avatarUrl")}</Label>
							<Input
								id={"avatar-url"}
								type={"url"}
								placeholder={"https://example.com/avatar.png"}
								value={avatarUrl}
								onChange={(e) => setAvatarUrl(e.target.value)}
								disabled={isLoading}
							/>
						</div>
						<Button
							type={"submit"}
							className={"w-full"}
							disabled={isLoading || saving}
						>
							{saving ? t("auth.saving") : t("auth.update")}
						</Button>
						<Divider />
						<Button
							type={"button"}
							variant={"outline"}
							className={"w-full"}
							onClick={handleSignOut}
						>
							{t("auth.signOut")}
						</Button>
					</form>
				</CardContent>
			</Card>
		</Page>
	)
}
